import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { projectApi } from '@/api/projectApi';
import { getAuthHeaders as getAuthHeadersAsync } from '@/api/workosAuth';
import { API_BASE_URL } from '@/config';
import { useSettings } from '@/contexts/SettingsContext';
import { formatDateByPreference } from '@/utils/dateFormat';
import { decodeUnicodeEscapes } from '@/utils/decodeUnicode';
import { TableScaffold } from '@/components/ui/TableScaffold';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

interface SnapshotRow {
  id: string;
  snapshot_name?: string;
  file_name?: string;
  uploaded_at?: string;
  compile_status?: string | null;
  is_baseline?: boolean;
  is_active?: boolean;
}

const BaselinesPage: React.FC = () => {
  const { selectedProject, refreshBaselines, setSelectedBaselineId } = useGlobalProject();
  const { settings } = useSettings();
  const [snapshots, setSnapshots] = useState<SnapshotRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [baselineName, setBaselineName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [snapshotToRemove, setSnapshotToRemove] = useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [settingActiveId, setSettingActiveId] = useState<string | null>(null);

  const fetchSnapshots = useCallback(async () => {
    const projectId = selectedProject?.id;
    if (!projectId) {
      setSnapshots([]);
      return;
    }
    setLoading(true);
    try {
      const headers = await getAuthHeadersAsync();
      const res = await fetch(`${API_BASE_URL}/v1/snapshots/${encodeURIComponent(projectId)}`, {
        headers,
      });
      if (!res.ok) {
        setSnapshots([]);
        return;
      }
      const data = await res.json();
      const all = (data.snapshots || []) as SnapshotRow[];
      const baselineOnly = all.filter(
        (s) => s.is_baseline === true || (s as { snapshot_type?: string }).snapshot_type === 'baseline'
      );
      setSnapshots(baselineOnly);
    } catch {
      setSnapshots([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  const handleSetActive = async (snapshotId: string) => {
    setSettingActiveId(snapshotId);
    try {
      await setSelectedBaselineId(snapshotId);
      await fetchSnapshots();
      await refreshBaselines();
    } finally {
      setSettingActiveId(null);
    }
  };

  const handleDownload = async (snapshotId: string, fileName: string) => {
    const projectId = selectedProject?.id;
    if (!projectId) return;
    try {
      const headers = await getAuthHeadersAsync();
      const res = await fetch(
        `${API_BASE_URL}/v1/snapshots/${encodeURIComponent(projectId)}/${snapshotId}/download`,
        { headers }
      );
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName?.endsWith('.xer') ? fileName : `${fileName || 'snapshot'}.xer`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch {
      // ignore
    }
  };

  const handleRemove = async () => {
    if (!snapshotToRemove) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      const headers = await getAuthHeadersAsync();
      const res = await fetch(`${API_BASE_URL}/v1/snapshots/${snapshotToRemove.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to remove snapshot');
      }
      setSnapshotToRemove(null);
      await fetchSnapshots();
      await refreshBaselines();
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove snapshot');
    } finally {
      setRemoving(false);
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedProject || !uploadFile) return;
    if (!baselineName.trim()) {
      setUploadError('Baseline name is required.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      await projectApi.uploadXerNewProject(selectedProject.name, uploadFile, {
        projectId: selectedProject.id,
        isBaseline: true,
        description: baselineName.trim(),
        organization: selectedProject.organization,
      });
      setShowUploadModal(false);
      setUploadFile(null);
      setBaselineName('');
      await fetchSnapshots();
      await refreshBaselines();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const formatCreated = (uploaded_at?: string) => {
    if (!uploaded_at) return '—';
    return formatDateByPreference(uploaded_at, settings.dateFormat, {
      showTime: true,
      timeZone: settings.timezone,
      use24Hour: settings.use24Hour,
    });
  };

  const hasProject = Boolean(selectedProject?.id);
  const isEmpty = hasProject && !loading && snapshots.length === 0;

  return (
    <MainLayout title="Baselines">
      <div className="flex flex-col flex-1 min-h-0 p-6">
        <PageHeader
          title="Baselines"
          subtitle="Compare schedule snapshots against a named baseline."
          actions={
            hasProject ? (
              <Button
                size="sm"
                onClick={() => {
                  setShowUploadModal(true);
                  setUploadFile(null);
                  setBaselineName('');
                  setUploadError(null);
                }}
                className="shrink-0"
              >
                <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                Upload baseline snapshot
              </Button>
            ) : null
          }
        />

        {!hasProject && (
          <div className="rounded-md border border-border bg-card p-8 text-center">
            <p className="text-sm text-muted-foreground">
              Select a project from the top bar to manage baselines.
            </p>
            <Link to="/app/projects" className="text-sm text-primary hover:underline mt-2 inline-block">
              Go to Projects
            </Link>
          </div>
        )}

        {hasProject && isEmpty && (
          <div className="rounded-md border border-border bg-card p-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              No baseline snapshots yet. Upload a P6 XER snapshot to create your first baseline.
            </p>
            <Button
              onClick={() => {
                setShowUploadModal(true);
                setUploadFile(null);
                setBaselineName('');
                setUploadError(null);
              }}
            >
              <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
              Upload baseline snapshot
            </Button>
          </div>
        )}

        {hasProject && !isEmpty && (
          <TableScaffold
            toolbar={
              <div className="flex items-center justify-between w-full">
                <span className="text-xs font-medium text-muted-foreground">Baseline snapshots</span>
                <span className="text-[10px] text-muted-foreground/60">{snapshots.length} snapshot(s)</span>
              </div>
            }
            className="mt-4"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                    >
                      Created
                    </th>
                    <th
                      scope="col"
                      className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                    >
                      Active
                    </th>
                    <th
                      scope="col"
                      className="py-3 px-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {snapshots.map((s) => (
                    <tr key={s.id} className="hover:bg-muted/30">
                      <td className="py-3 px-4 text-sm text-foreground">
                        {decodeUnicodeEscapes(s.snapshot_name || s.file_name) || '—'}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground whitespace-nowrap">
                        {formatCreated(s.uploaded_at)}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {s.compile_status ?? '—'}
                      </td>
                      <td className="py-3 px-4">
                        {s.is_baseline ? (
                          <Badge variant="secondary" className="text-xs">
                            <CheckCircleIcon className="h-3 w-3 mr-1 inline" />
                            Active
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!s.is_baseline && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSetActive(s.id)}
                              disabled={settingActiveId !== null}
                            >
                              {settingActiveId === s.id ? 'Setting…' : 'Set as active'}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(s.id, s.file_name || s.snapshot_name || 'snapshot')}
                            title="Download XER"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setSnapshotToRemove({
                                id: s.id,
                                name: s.snapshot_name || s.file_name || 'this snapshot',
                              })
                            }
                            title="Remove"
                            disabled={s.is_baseline}
                            className="text-destructive hover:text-destructive"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TableScaffold>
        )}

        {hasProject && loading && snapshots.length === 0 && (
          <div className="rounded-md border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            Loading snapshots…
          </div>
        )}
      </div>

      {/* Upload modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload baseline snapshot</DialogTitle>
            <DialogDescription>
              Upload a P6 XER file as a named baseline. It will be parsed and stored for comparison.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Baseline name *</label>
              <Input
                value={baselineName}
                onChange={(e) => {
                  setBaselineName(e.target.value);
                  setUploadError(null);
                }}
                placeholder="e.g. Contract baseline, March 2025"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">XER file</label>
              <Input
                type="file"
                accept=".xer"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setUploadFile(f || null);
                  setUploadError(null);
                }}
              />
              {uploadFile && (
                <p className="text-xs text-muted-foreground">
                  {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
            {uploadError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {uploadError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadModal(false);
                setUploadFile(null);
                setBaselineName('');
                setUploadError(null);
              }}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUploadSubmit}
              disabled={!uploadFile || !baselineName.trim() || uploading}
            >
              {uploading ? 'Uploading…' : 'Upload baseline snapshot'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation */}
      <Dialog
        open={!!snapshotToRemove}
        onOpenChange={(open) => {
          if (!open) {
            setSnapshotToRemove(null);
            setRemoveError(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove snapshot</DialogTitle>
            <DialogDescription>
              Remove &quot;{snapshotToRemove?.name}&quot; from the list? It will no longer appear in the
              baseline dropdown. The data is retained but hidden.
            </DialogDescription>
          </DialogHeader>
          {removeError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {removeError}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSnapshotToRemove(null);
                setRemoveError(null);
              }}
              disabled={removing}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemove} disabled={removing}>
              {removing ? 'Removing…' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default BaselinesPage;
